import { Message, User } from '../store/dbTypes'
import {
  removeFbPushNotificationSubscription,
  sendFbPushNotification,
  storeFbPushNotificationSubscription,
} from './fbPushNotificationsFunctions'
import { getUser } from '../User/user.model'
import { getWalk } from '../Walks/walks.model'
import { db } from '../store/db'
import { log } from '../common/utils'
import {
  addQueuedMessage,
  getQueuedMessages,
  removeQueuedMessage,
} from '../common/appStatus.model'
import { osName, isMobile, browserName } from 'react-device-detect'
import { createStandaloneToast } from '@chakra-ui/react'

const { toast } = createStandaloneToast()

// Get the subscription for this device
export const getPushNotificationsSubscription = async (user?: User) => {
  if (!user) return
  const registration = await navigator.serviceWorker.ready
  const subscription = await registration.pushManager.getSubscription()
  return refreshPushNotificationsSubscription(subscription, user)
}

// Check if the current subscription is valid. If not, refresh it
const refreshPushNotificationsSubscription = async (
  subscription: PushSubscription | null | undefined,
  user: User
) => {
  const existingPermission = Notification.permission
  if (existingPermission !== 'granted') return null // User hasn't allowed notifications
  if (!subscription) {
    // The previous subscription was expired, so get a new one
    const newSubscription = await subscribeToPushNotifications(user)
    console.log(
      'Re-subscribing to push notifications:',
      newSubscription ? 'Success' : 'failed'
    )
    return newSubscription
  }
  return subscription
}

// Unsubscribe from the existing subscription and remove from the backend
export const unsubscribeFromPushNotifications = async (user: User) => {
  const existingSubscription = await getPushNotificationsSubscription(user)
  if (existingSubscription) {
    await existingSubscription.unsubscribe()
    await removeFbPushNotificationSubscription(user, existingSubscription)
    console.log('Successfully unsubscribed from push notifications')
  }
}

// Request permission for push notifications and get a subscription
export const subscribeToPushNotifications = async (
  user: User
): Promise<PushSubscription | null> => {
  const registration = await navigator.serviceWorker.ready
  try {
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: import.meta.env.VITE_PUBLIC_VAPID_KEY,
    })
    const result = await storeFbPushNotificationSubscription(user, subscription)
    console.log(
      `${user.fullName} is${
        result ? ' ' : ' NOT '
      }subscribed/updated to push notifications`
    )
    return result ? subscription : null
  } catch (error) {
    await unsubscribeFromPushNotifications(user)
    console.error(
      'Error occurred while subscribing to push notifications. Possibly the key has changed. Attempting to unsubscribe from previous...',
      error
    )
    alert('Notifications configuration has changed. Please refresh the page')
    return null
  }
}

const sendTo1User = async (
  toId: string,
  fromUserName: string,
  message: string
) => {
  const toUser = await getUser(toId)
  if (!toUser || toUser.status !== 'active' || toUser.isBlocked) return
  const sendResult = await sendFbPushNotification(
    toUser,
    `From ${fromUserName || '(error - unknown)'}`,
    message
  )

  if (!sendResult) {
    await log(`EPUSH ${message}`, true, { userId: toUser.userId })
  }
}

const queueTheMessage = async (message: Message) => {
  if (navigator.onLine) return 'canSend'

  // We dont have internet, so set up a sync event to fire (in service worker) when internet is regained (only on Android)
  const registration = await navigator.serviceWorker.ready
  try {
    await registration.sync.register('send-queued-messages')
  } catch {
    await log(
      'Send-queued-messages event could not be registered (usual for iOS)'
    )
  }

  // Add the message to the queue
  const queuedMessages: Message[] = await getQueuedMessages()
  if (queuedMessages.find((m) => m.messageId === message.messageId)) {
    // This message already queued, so no need to queue it again
    return 'queued'
  }

  await addQueuedMessage(message)
  return 'queued'
}

export const sendPushNotificationsForMessage = async (message: Message) => {
  const fromUser = await getUser(message.fromUserId)
  if (!fromUser) return

  const result = await queueTheMessage(message)
  if (result === 'queued') return result

  if (message.to === 'all') {
    const usersDexie = await db.users.toArray()
    if (!usersDexie) return
    for (const user of usersDexie) {
      await sendTo1User(user.userId, fromUser.fullName, message.message)
    }
  }

  if (message.to === 'walk') {
    const walk = await getWalk(message.toId)
    if (!walk) return
    for (const walkerId of walk.walkers) {
      await sendTo1User(walkerId, fromUser.fullName, message.message)
    }
  }

  if (message.to === 'user') {
    await sendTo1User(message.toId, fromUser.fullName, message.message)
  }

  return 'sent'
}

export const togglePushNotifications = async (
  user?: User,
  toStatus?: 'on' | 'off'
): Promise<boolean> => {
  if (!user) return false
  if (toStatus === 'on') {
    const permission: NotificationPermission =
      await Notification.requestPermission()
    if (permission === 'granted') {
      await subscribeToPushNotifications(user)
    } else if (permission === 'denied') {
      const platform = isMobile ? osName : browserName
      toast({
        title: 'System Notifications',
        description: `First, you need to enable permissions for notifications for this app from within ${platform}. Then, try again here`,
        isClosable: true,
        duration: null,
        position: 'top',
      })
      return false
    }
  } else if (toStatus === 'off') {
    await unsubscribeFromPushNotifications(user)
  }
  return true
}

export const clearQueuedMessages = async () => {
  // Find queued messages
  const appStatus = await db.appStatus.get('Tripsheets')
  if (!appStatus?.haveInternet) return 0

  const queuedMessages = appStatus.messagesQueue || []

  // Send them now and remove from queue
  let count = 0
  for (const message of queuedMessages) {
    const result = await sendPushNotificationsForMessage(message)
    if (result === 'sent') {
      await removeQueuedMessage(message)
      count++
    }
  }

  return count
}
